/**
 * Provides access to API models related to **TBD**
 *
 * @summary _Short summary to be provided_
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults, IApiSettingsResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Toaster } from 'app/shared/lib/toaster';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IUserSearchFilters {
    generalSearchTerm?: string | null;
    hasEmail?: boolean | null;
    hasLogin?: boolean | null;
    hasNoExternalSystem?: boolean | null;
    notInternal?: boolean | null;
    login?: string | null;
    application?: Core.Application | null;
    loginBetweenStart?: string | null;
    loginBetweenEnd?: string | null;
    userFirstName?: string | null;
    userLastName?: string | null;
    emailAddress?: string | null;
    title?: string | null;
    department?: string | null;
    active?: boolean | null;
}

export function countUserFilters(searchFilters: IUserSearchFilters | null = null): number {
    if(!searchFilters) { return 0; }

    // TODO: we only ever make decisions based on count===0 or count>0, so this could be hasMemberFilters(): boolean
    // then a helper function like hasPropertyWhere(searchFilters, truthy, 'generalSearchTerm', ...) could do the
    // bulk of the work
    let count = 0;
    if(searchFilters.login) { count += 1; }
    if(searchFilters.application) { count += 1; }
    if(searchFilters.loginBetweenStart) { count += 1; }
    if(searchFilters.loginBetweenEnd) { count += 1; }
    if(searchFilters.userFirstName) { count += 1; }
    if(searchFilters.userLastName) { count += 1; }
    if(searchFilters.emailAddress) { count += 1; }
    if(searchFilters.title) { count += 1; }
    if(searchFilters.department) { count += 1; }
    if(searchFilters.active) { count += 1; }

    return count;
}

@transient()
@autoinject()
export class UserService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(protected readonly api: Api,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster
    ) { }


    ////****************** Queries **************************************************////

    /**
     * Load all `Core.User` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadUsers(options?: Partial<IQueryLoadOptions>, includes: string[] = [], searchFilter?: IUserSearchFilters, fields: string[] = []): Promise<IApiQueryResults<Core.User>> {
        const query = this.api
            .query('users')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchFilter?.notInternal) {
            query.filterBoolean('IsInternal', false)
        }

        if(searchFilter?.hasNoExternalSystem) {
            query.filterIsNull('externalSystem.id', true);
        }

        if(searchFilter?.hasEmail) {
            query.filterIsNull('emailAddress', false);
        }

        if(searchFilter?.hasLogin) {
            query.filterIsNull('login.id', false);
        }

        return this.api.get(query);
    }

    public async search(
        searchFilters: IUserSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []): Promise<IApiQueryResults<Core.User>> {

        const count = countUserFilters(searchFilters);
        if(count === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria('core:user.$');
            return createQueryResults('Core.User', [], true);
        }

        const query = this.api
            .query('users')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);


        if(count > 0) {
            if(searchFilters.generalSearchTerm) {
                query.search(searchFilters.generalSearchTerm);
            }
            else {
                if(searchFilters.login) {
                    query.filterString(
                        'login.loginName',
                        searchFilters.login,
                        'contains');
                }
                if(searchFilters?.hasNoExternalSystem) {
                    query.filterIsNull('externalSystem.id', true);
                }
                if(searchFilters?.notInternal) {
                    query.filterBoolean('isInternal', false);
                }
                if(searchFilters.application) {
                    query.filterExists('roles', q => q.filterString('application.code', searchFilters.application?.code ? searchFilters.application?.code : null));
                }
                if(searchFilters.loginBetweenStart && searchFilters.loginBetweenEnd) {
                    query.filterDateTimeBetweenDates('login.lastLogInTimestamp', searchFilters.loginBetweenStart, searchFilters.loginBetweenEnd || null);
                }
                if(searchFilters.userFirstName) {
                    query.filterString(
                        'name.first',
                        searchFilters.userFirstName,
                        'startsWith'
                    );
                }
                if(searchFilters.userLastName) {
                    query.filterString(
                        'name.last',
                        searchFilters.userLastName,
                        'startsWith');
                }
                if(searchFilters.emailAddress) {
                    query.filterString(
                        'emailAddress',
                        searchFilters.emailAddress,
                        'startsWith');
                }
                if(searchFilters.title) {
                    query.filterString(
                        'title',
                        searchFilters.title,
                        'startsWith');
                }
                if(searchFilters.department) {
                    query.filterString(
                        'department',
                        searchFilters.department,
                        'startsWith');
                }
                if(!searchFilters.active) {
                    query.includeInactive(true);
                }
                else {
                    query.includeInactive(false);
                }
            }
        }

        return await this.api.get(query);
    }

    /**
     * Loads UserRoles based on the application
     */
    public async loadUserRolesByApplication(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.UserRole>> {
        const query = this.api
            .query('users/roles')
            .filterSession('application.id', 'application.id')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return this.api.get(query);
    }

    /**
     * Loads a specific UserContext by filtering on UserID
     */
    public async loadUserContext(userId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.UserContext>> {
        const query = this.api
            .query(`users/contexts`)
            .filterString('user.id', userId, 'eq')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return this.api.get(query);
    }

    ////****************** Loads ****************************************************////
    public async loadUser(userId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.User>> {
        const query = this.api
            .query(`users/${userId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadAssociatedProviders(userId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.ProviderUserContext>> {
        const query = this.api
            .query(`cd/providers/users/contexts`)
            .filterString('user.id', userId, 'eq')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadAssociatedProviderContext(providerId: string, userId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.ProviderUserContext>> {
        const query = this.api
            .query(`cd/providers/users/contexts`)
            .filterString('user.id', userId, 'eq')
            .filterString('provider.id', providerId, 'eq')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSignOnProviders(includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.SignOnProvider>> {
        const query = this.api
            .query(`signon/providers/`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSignOnProvidersByCode(includes: string[] = [], fields: string[] = [], signOnProviderCode: string): Promise<IApiQueryResults<Core.SignOnProvider>> {
        const query = this.api
            .query(`signon/providers/`)
            .filterString('method.code', signOnProviderCode, 'eq')
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSignOnProviderSettings(id: string): Promise<IApiSettingsResults> {
        return this.api.get(
            this.api.settings(`signon/providers/${id}`)
        );
    }

    ////****************** Actions **************************************************////
    public async saveUser(user: Core.User): Promise<IApiActionResults> {
        const actionName = !user.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `users/${actionName === 'update' ? user.id : ''}`, user);

        return await this.api.post(action);
    }

    public async saveUserContext(userContext: Core.UserContext): Promise<IApiActionResults> {
        const actionName = !userContext.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `users/contexts/${actionName === 'update' ? userContext.id : ''}`, userContext);

        return await this.api.post(action);
    }

    public async activateUser(userId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `users/${userId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateUserContext(userContextId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `users/contexts/${userContextId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async loadUserSettings(): Promise<any> {
        const query = this.api
            .query(`users/!settings`);

        return await this.api.get(query);
    }

    public async loadLocations(options?: Partial<IQueryLoadOptions>): Promise<any> {
        const query = this.api
            .query('locations/')
            .applyStandardLoadOptions(options);

        return await this.api.get(query);
    }

    public async loadFilters(options?: Partial<IQueryLoadOptions>): Promise<any> {
        const query = this.api
            .query('filters/')
            .applyStandardLoadOptions(options);

        return await this.api.get(query);
    }

    public async activateAssociatedProvider(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/providers/users/contexts/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);

        } else {
            return await this.api.delete(action);
        }
    }

    public async saveAssociatedProvider(providerContext: CD.ProviderUserContext): Promise<IApiActionResults> {
        const actionName = !providerContext.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/providers/users/contexts/${actionName === 'update' ? providerContext.id : ''}`, providerContext);

        return await this.api.post(action);
    }
}
